import CustomSelectMenu from "@components/FormFields/CustomSelectMenu";
import { LocationSelectMenu } from "@components/Locations/LocationSelectMenu";
import { Button } from "@components/ui/button";
import { Checkbox } from "@components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@components/ui/dialog";
import { ScrollArea } from "@components/ui/scroll-area";
import { useAuth } from "@hooks/useAuth";
import { useLocations } from "@hooks/useLocations";
import { usePlans } from "@hooks/usePlans";
import { ScreenWithPlayingInfo, useScreens } from "@hooks/useScreens";
import { env } from "@src/env.mjs";
import { useMutation } from "@tanstack/react-query";
import { useMusicLocation, useMusicZone } from "@utils/queries";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Location } from "types";

export function MusicSignupDialog({
    onAccept,
    onCancel,
    locations,
    defaultLocationId,
    isAdmin,
    screen,
    onAddLocation,
}: {
    onAccept: (locationId: string, status: 'active' | 'demo') => Promise<void>;
    onCancel: () => void;
    locations: Location[];
    defaultLocationId?: string;
    isAdmin: boolean;
    screen: ScreenWithPlayingInfo;
    onAddLocation?: (locationId: string) => void;
}) {
    const [accepted, setAccepted] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState<Location | undefined>(
        locations.find(l => l.id === defaultLocationId)
    );
    useEffect(() => {
        defaultLocationId && setSelectedLocation(locations.find(l => l.id === defaultLocationId))
    }, [defaultLocationId, locations])
    const [musicStatus, setMusicStatus] = useState<'active' | 'demo'>('demo');

    const { getMusicCost, getMusicStatus } = usePlans();
    const [showEula, setShowEula] = useState(false);

    const currentStatus = getMusicStatus();
    const baseCost = getMusicCost();
    const { organization } = useAuth()

    const createMusicZone = useMusicZone.create();
    const createMusicLocation = useMusicLocation.create();
    const { updateScreen } = useScreens();
    const { updateLocation } = useLocations()

    const { mutate: handleAccept, isPending: isLoading } = useMutation({
        mutationFn: async () => {
            if (!selectedLocation) return;
            const status = isAdmin ? musicStatus : (env.NEXT_PUBLIC_IS_DEV ? "demo" : "active");

            // Create (or recreate) the CustomChannels location and persist its id_code.
            const provisionLocation = async () => {
                const locationData = await createMusicLocation.mutateAsync({
                    ...selectedLocation,
                    name: organization?.name ?? "Anonymous Location",
                    status: status,
                });
                if (locationData.status !== "success") throw new Error(locationData.message);
                updateLocation({ id: selectedLocation.id, id_code: locationData.location.id });
                return locationData.location.id;
            };

            let locationIdCode = selectedLocation.id_code || (await provisionLocation());

            if (!screen.zone_id && locationIdCode) {
                type ZoneResult = Awaited<ReturnType<typeof createMusicZone.mutateAsync>>;
                const writeZone = (zoneData: ZoneResult) => {
                    if (zoneData.status === "success") {
                        updateScreen({
                            screenId: screen.id,
                            data: {
                                name: screen.name ?? "",
                                zone_id: zoneData.zone.id,
                                music_infos: [zoneData.zone],
                            }
                        });
                    }
                };

                try {
                    writeZone(await createMusicZone.mutateAsync({ location_id: locationIdCode, name: screen.name, status }));
                } catch (err) {
                    // The stored CustomChannels location was removed on the CC side (the
                    // usual cause of a stale integration / 401). Recreate it once and retry
                    // so re-adding music always succeeds instead of failing with no zone_id.
                    const message = (err instanceof Error ? err.message : String(err)).toLowerCase();
                    const locationMissing = message.includes("not found") || message.includes("location");
                    if (!locationMissing) throw err;
                    locationIdCode = await provisionLocation();
                    writeZone(await createMusicZone.mutateAsync({ location_id: locationIdCode, name: screen.name, status }));
                }
            }

            await onAccept(selectedLocation.id, status);
        },
    });

    return (
        <div className="flex flex-col gap-4 p-4">
            <Dialog open={showEula} onOpenChange={setShowEula}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>Terms of Service and End User License Agreement</DialogTitle>
                    </DialogHeader>
                    <ScrollArea className="h-[60vh] w-full rounded-md">
                        <div className="flex flex-col gap-4 p-4 text-justify text-sm leading-relaxed">
                            <h2 className="font-semibold text-sm text-left">
                                END-USER LICENSING AGREEMENT FOR CUSTOMCHANNELS.NET MUSIC STREAMING SERVICES
                            </h2>
                            <p className="text-muted-foreground text-xs text-left">
                                These Terms and Conditions ("Terms and Conditions") shall govern and apply to the sale by VAR of Services to End-User intended for use as on-premise background music in the End-User's places of business ("End-User Locations"). The Services are designed for delivery to the End-User Locations over the internet by means of streaming technologies through the authorized digital signage hardware and/or software provided from VAR. The term "Service" also includes the client portal at client.customchannels.net and any other technology, platform, API, and Third-Party Content (as described below) which may be included as part of the Service. These Terms and Conditions shall be made a part of and incorporated into any service agreement, order, order form, electronic order, purchase order, order acknowledgement, invoice, agreement or contract (each of the foregoing, together with these Terms and Conditions, the "Agreement").
                            </p>
                            <div className="space-y-3">
                                <div>
                                    <h3 className="font-medium mb-1.5 text-sm">1. Licenses to use Third-Party Content</h3>
                                    <p className="pl-3 text-muted-foreground">
                                        a) The sound recordings and musical compositions provided as part of the Services ("Third-Party Content") are the property of third-party rights holders and are made available for use by special licensing arrangements. The term "Licenses" includes, but is not limited to, Licenses issued by Performing Rights Organizations ("PROs") such as ASCAP, BMI and others in the U.S. and SOCAN and Resound in Canada, authorizing the non-dramatic public performance of musical works within each PROs repertoire at each End-User Location by means of the Services in the U.S. and Canada.
                                    </p>
                                    <p className="pl-3 mt-2 text-muted-foreground">
                                        b) Provider will be responsible for obtaining, complying with and maintaining in full force and effect during the Agreement all Licenses necessary for the use of the Services at End-User Locations, including payment of all royalties and License fees associated therewith, provided End-User use of the Services complies with the following acceptable use terms, conditions and limitations as imposed by the PROs:
                                    </p>
                                    <ul className="list-disc pl-6 mt-2 space-y-0.5 text-muted-foreground">
                                        <li>End-User will only access the Services using a Provider-authorized media player and the audio from the Services will only be publicly performed via loudspeakers in approved End-User Locations within the jurisdiction of the Licenses (U.S., Canada, and their respective territories) and used as an accompaniment to routine activities, including, but not limited to work, shopping, conversation, dining and relaxation.</li>
                                        <li>The Services may not be used by a VJ/DJ, or as an integral part of an event, activity or venue where admission is charged, or as an accompaniment to any dramatic performance, dance, musicians, skate, cycle, aerobic, fitness or other similar activity.</li>
                                        <li>End-User may not record, reproduce, edit, adapt, repurpose, retransmit, broadcast or otherwise exploit the Third-Party Content other than expressly permitted herein.</li>
                                        <li>The Services may not be transmitted to any private home, apartment, guest room in a hotel or motel, or other similar location.</li>
                                        <li>End-User further acknowledges that these Licenses only apply to acceptable use of the Services.</li>
                                        <li>VAR and/or Provider reserve the right to suspend Services at any End-User Location that is believed to be using Services in a manner outside the approved uses.</li>
                                    </ul>
                                </div>
                                <div>
                                    <h3 className="font-medium mb-1.5 text-sm">2. Music Services</h3>
                                    <p className="pl-3 text-muted-foreground">
                                        VAR shall provide the Services to each End-User Location pursuant to the Agreement. Some programming and/or channels available through the Services may include content which may not be appropriate for End-User's patrons. It is End-User's responsibility to impose listening restrictions that End-User considers appropriate for its environment.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </ScrollArea>
                    <div className="flex justify-end gap-2 mt-4">
                        <Button
                            variant="outline"
                            onClick={() => {
                                setAccepted(false);
                                setShowEula(false);
                            }}
                        >
                            Decline
                        </Button>
                        <Button
                            onClick={() => {
                                setAccepted(true);
                                setShowEula(false);
                            }}
                        >
                            Accept
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            <div className="flex flex-col gap-4 mb-4">
                <div className="rounded-lg border p-4 space-y-3">
                    <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">Music Integration Cost</span>
                        <span className="font-medium">${baseCost}/month</span>
                    </div>

                    {currentStatus === "demo" ? (
                        <div className="space-y-3">
                            <div className="bg-yellow-50 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-200 p-4 rounded-md text-sm border border-yellow-200 dark:border-yellow-800">
                                <p className="font-medium mb-1">Free Trial Period</p>
                                <ul className="list-disc pl-4 space-y-1">
                                    <li>Try our music integration features at no cost during your trial period</li>
                                    <li>After the trial period ends, you'll be charged ${baseCost}/month to continue using music integration</li>
                                    <li>You can cancel anytime during the trial</li>
                                </ul>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            <div className="bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200 p-4 rounded-md text-sm border border-blue-200 dark:border-blue-800">
                                <p className="font-medium mb-1">Subscription Details</p>
                                <ul className="list-disc pl-4 space-y-1">
                                    <li>First screen music integration: ${baseCost}/month</li>
                                    <li>Each additional screen: ${baseCost * 0.8}/month</li>
                                    <li>Billed monthly with your current subscription</li>
                                    <li>Cancel anytime - no long-term commitment required</li>
                                </ul>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className="flex flex-col gap-4">
                <LocationSelectMenu
                    optionRender={(val) => val.name}
                    selectedRender={(val) => val.name}
                    selected={selectedLocation}
                    options={locations}
                    onChange={l => l && setSelectedLocation(l)}
                    onAddLocation={onAddLocation}
                />

                {isAdmin && (
                    <CustomSelectMenu
                        label="Status"
                        options={['active', 'demo'] as const}
                        optionRender={(val) => val}
                        selectedRender={(val) => val}
                        selected={musicStatus}
                        onChange={s => s && setMusicStatus(s)}
                    />
                )}

                <div className="flex items-center gap-2">
                    <Checkbox
                        id="eula"
                        checked={accepted}
                        onCheckedChange={(checked) => setAccepted(checked as boolean)}
                        disabled={isLoading}
                    />
                    <label htmlFor="eula" className="text-sm">
                        By signing up for music streaming service with Custom Channels, you agree to the {' '}
                        <button
                            onClick={() => setShowEula(true)}
                            className="text-primary hover:underline"
                            type="button"
                        >
                            Terms of Use
                        </button>
                    </label>
                </div>
            </div>

            <div className="flex justify-end gap-2">
                <Button
                    variant="outline"
                    onClick={onCancel}
                    disabled={isLoading}
                >
                    Cancel
                </Button>
                <Button
                    onClick={() => handleAccept()}
                    className="animate-[width]"
                    disabled={!accepted || !selectedLocation || isLoading}
                >
                    {isLoading ? (
                        <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            {currentStatus === "active" ? "Setting up music integration..." : "Setting up music..."}
                        </>
                    ) : (
                        currentStatus === "active" ? `Enable Music ($${baseCost}/month)` : 'Enable Music'
                    )}
                </Button>
            </div>
        </div>
    );
}